/**
 * Maps domain states to the design system's semantic tones.
 *
 * Centralised so that a state means the same colour on every screen. A state
 * that reads amber in the command centre and grey in the task list teaches an
 * operator two conflicting things about the same fact.
 */

import type { Tone } from '@/components/primitives'
import type { SessionActivity } from './sessionActivity'
import type {
  AccountState,
  TaskState,
  Confidence,
  ModelAvailability,
  ProjectState,
  SessionKind,
  SessionState,
  MissionState,
  RunState,
  StartOutcome,
  SupervisorOutcome,
} from '@/types/domain'

export function accountStateTone(state: AccountState): Tone {
  switch (state) {
    case 'READY':
      return 'ok'
    case 'BUSY':
    case 'LOW_HEADROOM':
    case 'RESET_PENDING':
      return 'warn'
    case 'EXHAUSTED':
    case 'AUTH_REQUIRED':
    case 'DEGRADED':
      return 'err'
    case 'UNCONFIGURED':
    case 'DISABLED':
      return 'neutral'
    case 'UNKNOWN':
      // Deliberately not neutral. An account whose health was never established
      // is not "fine but idle", and the colour must not suggest that.
      return 'unknown'
  }
}

export function taskStateTone(state: TaskState): Tone {
  switch (state) {
    case 'RUNNING':
    case 'STARTING':
    case 'LEASED':
      return 'accent'
    case 'COMPLETED':
      return 'ok'
    case 'FAILED_FINAL':
    case 'FAILED_RETRYABLE':
      return 'err'
    case 'WAITING_FOR_HUMAN':
    case 'APPROVAL_REQUIRED':
    case 'AUTH_REQUIRED':
    case 'RATE_LIMITED':
    case 'POLICY_BLOCKED':
    case 'REWORK_REQUIRED':
    case 'HOST_UNREACHABLE':
      return 'warn'
    case 'REVIEWING':
    case 'HANDOFF_PENDING':
    case 'WAITING_FOR_AGENT':
      return 'info'
    default:
      return 'neutral'
  }
}

export function confidenceTone(c: Confidence): Tone {
  switch (c) {
    case 'EXACT':
      return 'ok'
    case 'ESTIMATED':
      return 'info'
    case 'INFERRED':
      return 'warn'
    case 'UNKNOWN':
      return 'unknown'
  }
}

/**
 * Tone for a usage bar, which shows the percent a rate window is USED — the
 * inverse of capacity, so this must NOT reuse capacityTone (which reads a
 * remaining fraction). The provider's own severity wins when it escalates;
 * otherwise the fill decides: a nearly-full window is a warning even while the
 * provider still calls it "normal".
 */
export function usageSeverityTone(severity: string | undefined, utilization: number): Tone {
  switch ((severity ?? '').toLowerCase()) {
    case 'critical':
    case 'exhausted':
      return 'err'
    case 'warning':
      return 'warn'
  }
  if (utilization >= 90) return 'err'
  if (utilization >= 70) return 'warn'
  return 'ok'
}

export function availabilityTone(a: ModelAvailability): Tone {
  switch (a) {
    case 'AVAILABLE':
      return 'ok'
    case 'DEGRADED':
      return 'warn'
    case 'UNAVAILABLE':
    case 'BLOCKED':
      return 'err'
    case 'UNVERIFIED':
      // Unverified is not "probably fine". The router treats it as ineligible
      // for high-risk work, and the matrix must say so at a glance.
      return 'unknown'
  }
}

/**
 * A dependency's tone, from the two booleans the server actually reports.
 *
 * A critical dependency being down is an error: writes stop. A non-critical one
 * being down is a warning: something is degraded but the system still works.
 * Collapsing both into one "down" colour would tell an operator that a stale
 * telemetry feed is as urgent as an unreachable database.
 */
export function dependencyTone(dep: { up: boolean; critical: boolean }): Tone {
  if (dep.up) return 'ok'
  return dep.critical ? 'err' : 'warn'
}

/** The wording that goes beside the dot. Colour alone is never the signal. */
export function dependencyLabel(dep: { up: boolean; critical: boolean }): string {
  if (dep.up) return 'UP'
  return dep.critical ? 'DOWN' : 'DEGRADED'
}

/** Overall health, from the server's "ok" / "degraded" string. */
export function healthTone(status: string): Tone {
  switch (status) {
    case 'ok':
      return 'ok'
    case 'degraded':
      return 'warn'
    case '':
      return 'neutral'
    default:
      // An unrecognised status is not assumed healthy.
      return 'warn'
  }
}

export function severityTone(s: string): Tone {
  switch (s) {
    case 'critical':
    case 'error':
      return 'err'
    case 'warning':
      return 'warn'
    case 'debug':
      return 'neutral'
    default:
      return 'info'
  }
}

/**
 * How loud a severity is, for picking the worst of several.
 *
 * Ranked here rather than at a call site because the ordering has to agree with
 * `severityTone` — a "worst" that the colour disagrees with is a panel header
 * showing a yellow dot over a red row. Unknown strings rank lowest: a severity
 * this console does not recognise must not outrank one it does.
 */
export function severityRank(s: string): number {
  switch (s) {
    case 'critical':
      return 4
    case 'error':
      return 3
    case 'warning':
      return 2
    case 'debug':
      return 0
    default:
      return 1
  }
}

export function riskTone(level: string): Tone {
  switch (level) {
    case 'L0_READ_ONLY':
      return 'neutral'
    case 'L1_ISOLATED_WRITE':
      return 'info'
    case 'L2_VALIDATED_CHANGE':
      return 'accent'
    case 'L3_EXTERNAL_OR_IRREVERSIBLE':
      return 'warn'
    case 'L4_FINANCIAL_OR_SECURITY':
      return 'err'
    default:
      // An unrecognised risk level renders at maximum severity, matching the
      // policy engine's fail-closed behaviour.
      return 'err'
  }
}

export function projectStateTone(state: ProjectState): Tone {
  switch (state) {
    case 'ACTIVE':
      return 'ok'
    case 'ONBOARDING':
      // Not neutral: onboarding is a project the scheduler is deliberately
      // holding work back from, which the operator has to be able to see.
      return 'info'
    case 'DEGRADED':
      return 'warn'
    case 'PAUSED':
    case 'ARCHIVED':
      return 'neutral'
    default:
      return 'unknown'
  }
}

/**
 * Tone for one git fact read from a host.
 *
 * Three states, not two: read and true, read and false, and never read. The last
 * one is why this exists — a repository nobody has scanned must not render the
 * same grey as one scanned and found to have no remote.
 */
export function readingTone(value: boolean | null | undefined): Tone {
  if (value == null) return 'unknown'
  return value ? 'ok' : 'neutral'
}

/**
 * Tone for what a session is doing.
 *
 * Working keeps the accent — the colour this interface already spends on "live,
 * and yours" — so a busy agent looks exactly as it did before. Waiting takes
 * info, because an agent asking for a decision is not a quieter kind of idle but
 * the one state that is *about* the operator. Idle steps down
 * to warn rather than to neutral, because an agent that has stopped is something
 * the operator is meant to notice and act on; grey would file it under scenery.
 * And unknown keeps its own colour, as everywhere else: a session nobody has
 * measured is not a quiet one.
 */
export function sessionActivityTone(activity: SessionActivity): Tone {
  switch (activity) {
    case 'WORKING':
      return 'accent'
    case 'WAITING':
      // Its own colour, not a louder amber. "The agent is waiting for you" and
      // "the agent has nothing to do" are opposite instructions to the operator,
      // and info is the vocabulary's existing word for "this is addressed to
      // you" — the same blue the console already uses for a deliberate hold.
      return 'info'
    case 'DONE':
      // The one state in this list that means nothing is wrong. `ok` is the
      // vocabulary's existing word for "this went as it should" — not `accent`,
      // which is "live, and yours"; not `warn`, which is what this state exists
      // to take *off* a normal ending; and not `neutral`, which is ENDED, a
      // session that will not speak again. This one will.
      return 'ok'
    case 'IDLE':
      // Not amber either, and for the same reason as ENDED below — the argument
      // was made there and then not applied here, which is how eight of eleven
      // cards on the deployed board came to wear the same warning colour.
      //
      // An agent between turns is the *resting* state of this fleet, not a
      // fault: measured on the deployment, eight of eleven sessions are IDLE at
      // any moment. A colour that is on three quarters of the screen carries no
      // information, and it crowds out the two or three things that do.
      // `reportsSilence` in dashboardModel refuses to colour silence past a
      // threshold on exactly this arithmetic; the tone map was doing it anyway,
      // one import away.
      //
      // Shares `neutral` with ENDED, which is safe because the two are told
      // apart in words on every surface that renders them: the state is printed
      // beside the light, and an ended session also carries `SessionEnded`.
      // How long an agent has been quiet is a *duration*, and it is stated
      // precisely in mono rather than encoded as a colour.
      return 'neutral'
    case 'ENDED':
      // Not amber. A session that finished did what it was asked; warning
      // colour on a normal ending is what made the list read as though half the
      // pool were stalled.
      return 'neutral'
    case 'PROBLEM':
      return 'err'
    case 'UNKNOWN':
      return 'unknown'
  }
}

/**
 * Tone for a session's stored state — the record's verdict, as distinct from
 * what the agent is doing right now (sessionActivityTone). Shared so the
 * cockpit, the project view and the dashboard put one colour on one state.
 */
export function sessionStateTone(state: SessionState): Tone {
  switch (state) {
    case 'RUNNING':
      return 'accent'
    case 'INPUT_REQUIRED':
      return 'warn'
    case 'FAILED':
      return 'err'
    case 'COMPLETED':
      return 'ok'
    case 'IDLE':
    case 'CHECKPOINTED':
      return 'info'
    case 'CREATED':
    case 'STARTING':
    case 'HANDING_OFF':
    case 'TERMINATED':
      return 'neutral'
  }
}

/** ADOPTED and OBSERVED are not decorations. Adopted means Kimene took over a
 *  session it did not start; observed means it cannot type into it at all. */
export function kindTone(kind: SessionKind): Tone {
  switch (kind) {
    case 'MANAGED':
      return 'accent'
    case 'ADOPTED':
      return 'info'
    case 'OBSERVED':
      return 'warn'
  }
}

/** Tone for a mission's state: the container's own lifecycle, one colour per
 *  state on every screen that shows one. */
export function missionStateTone(state: MissionState): Tone {
  switch (state) {
    case 'ACTIVE':
      return 'accent'
    case 'PLANNING':
    case 'REVIEWING':
      return 'info'
    case 'BLOCKED':
      return 'warn'
    case 'COMPLETED':
      return 'ok'
    case 'FAILED':
      return 'err'
    case 'DRAFT':
    case 'PAUSED':
    case 'CANCELLED':
      return 'neutral'
  }
}

/** Tone for a conductor run's state. */
export function runStateTone(state: RunState): Tone {
  switch (state) {
    case 'DECIDING':
    case 'APPROVED':
      return 'accent'
    case 'COMPLETED':
      return 'ok'
    case 'FAILED':
      return 'err'
    case 'PLANNED':
      return 'info'
    case 'CANCELLED':
      return 'neutral'
  }
}

/** Tone for one planned start's outcome — what became of a driven turn. */
export function startOutcomeTone(outcome: StartOutcome): Tone {
  switch (outcome) {
    case 'TURN_DONE':
      return 'ok'
    case 'WAITING':
      return 'info'
    case 'SUBMITTED':
    case 'STARTED':
      return 'accent'
    case 'TIMED_OUT':
      return 'warn'
    case 'FAILED':
    case 'SESSION_ENDED':
      return 'err'
    case 'SKIPPED':
      return 'neutral'
  }
}

/** A supervisor action's outcome. A refusal is not a failure - the world moved,
 *  and Kimene declined rather than writing from a state nobody decided about -
 *  so it reads as a warning, while a failed attempt reads as an error. */
export function supervisorOutcomeTone(outcome: SupervisorOutcome): Tone {
  switch (outcome) {
    case 'APPLIED':
      return 'ok'
    case 'RECORDED':
      return 'info'
    case 'REFUSED':
      return 'warn'
    case 'FAILED':
      return 'err'
    case 'AWAITING_OPERATOR':
      // Not a problem — it is the loop handing something back on purpose.
      return 'accent'
  }
}
